import { useCallback, useMemo } from "react";
import { useHospitalData } from "@/contexts/HospitalDataProvider";
import { useGetPccRooms } from "@/pages/Settings/DepartmentConfiguration/forms/hooks";
import { type CensusScopeProblem, censusScopeProblem, type EmrMapping, offeredEmrMapping, type RoomsListState } from "./locationEmrMapping";

/** What the popup shows in place of the EMR section while the rooms list holds Save back. */
export type RoomsNotice = Extract<CensusScopeProblem, "rooms_loading" | "rooms_failed">;

/** While the rooms list holds Save back, the EMR section has nothing to show yet, so the popup says why in its place. */
export const roomsNoticeFor = (scope: CensusScopeProblem | null): RoomsNotice | null => (scope === "rooms_loading" || scope === "rooms_failed" ? scope : null);

type CensusAreaOptions = {
	availableUnits: string[];
	availableFloors: string[];
	availableRooms: string[];
	/** Whether the department's EMR lists offer any unit, floor or room to map a location to. */
	emrOptionsAvailable: boolean;
	/** The part of `mapping` the EMR lists still offer, in their spelling (`offeredEmrMapping`). */
	offer: (mapping: EmrMapping) => EmrMapping;
	/** Why Save is blocked on the census choice, or null (`censusScopeProblem`). Pass the offered mapping. */
	scopeProblem: (offeredMapping: EmrMapping, departmentWideCensus: boolean) => CensusScopeProblem | null;
};

/** The EMR areas (units, floors, PCC rooms) a location's census can be read from, and the census-choice rules over them. */
export const useCensusAreaOptions = (): CensusAreaOptions => {
	const { hospitalData } = useHospitalData();
	// DEFAULT: [] — until the PCC rooms query answers there are no rooms to list. The census check reads
	// the query's own state instead, so it never mistakes "not loaded yet" for "no rooms".
	const { data: availableRooms = [], isLoading: roomsLoading, isError: roomsFailed } = useGetPccRooms();
	// DEFAULT: hospitalData is undefined until the department payload loads; until then there are no EMR areas to offer.
	const availableUnits = hospitalData?.units ?? [];
	const availableFloors = hospitalData?.floors ?? [];
	const unitsOrFloorsAvailable = availableUnits.length > 0 || availableFloors.length > 0;
	const emrOptionsAvailable = unitsOrFloorsAvailable || availableRooms.length > 0;
	const roomsCount = availableRooms.length;
	const roomsState = useMemo<RoomsListState>(
		() => ({ state: roomsLoading ? "loading" : roomsFailed ? "failed" : "loaded", count: roomsCount }),
		[roomsLoading, roomsFailed, roomsCount],
	);

	const offer = useCallback(
		(mapping: EmrMapping): EmrMapping =>
			offeredEmrMapping(mapping, {
				// DEFAULT: the lists are absent until the department payload loads; null keeps saved values until then.
				units: hospitalData?.units ?? null,
				floors: hospitalData?.floors ?? null,
				rooms: roomsLoading || roomsFailed ? null : availableRooms,
			}),
		[hospitalData?.units, hospitalData?.floors, roomsLoading, roomsFailed, availableRooms],
	);

	const scopeProblem = useCallback(
		(offeredMapping: EmrMapping, departmentWideCensus: boolean): CensusScopeProblem | null =>
			censusScopeProblem({ unitsOrFloorsAvailable, rooms: roomsState, mapping: offeredMapping, departmentWideCensus }),
		[unitsOrFloorsAvailable, roomsState],
	);

	return { availableUnits, availableFloors, availableRooms, emrOptionsAvailable, offer, scopeProblem };
};
